import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../utils/PermissionContext';
import { FaCalendarCheck, FaUserMd, FaTachometerAlt, FaUsers, FaCapsules, FaShoppingCart, FaTruck, FaBell, FaUser, FaSignOutAlt, FaCog } from 'react-icons/fa';
import { Dropdown, Image } from 'react-bootstrap';
import { getProfile } from '../utils/api';
import './TopNavigation.css';
import { BASE_URL } from '../utils/api';
import { DEFAULT_PROFILE_IMAGE } from '../config';

const TopNavigation = () => {
  const location = useLocation();
  const isAuthRoute = location.pathname === '/login' || location.pathname === '/forgot-password';
  const { hasPermission, loading, userRole } = usePermissions();
  const [profileImage, setProfileImage] = useState(DEFAULT_PROFILE_IMAGE);
  const [userInitial, setUserInitial] = useState(null);

  useEffect(() => {
    let mounted = true;
    const loadProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await getProfile();
        if (mounted && res?.success && res.data) {
          let img = res.data.profile_image || res.data.profile_image_url || res.data.avatar || res.data.image || null;
          if (img && typeof img === 'string' && img.startsWith('/')) {
            img = `${BASE_URL}${img}`;
          }
          setProfileImage(img || DEFAULT_PROFILE_IMAGE);
          setUserInitial(res.data.name?.charAt(0)?.toUpperCase() || null);
        }
      } catch (e) {
        // Set default image on error
        setProfileImage(DEFAULT_PROFILE_IMAGE);
      }
    };

    loadProfile();
    return () => { mounted = false; };
  }, []);

  const topNavRef = useRef(null);

  // Scroll active nav item into view when route or permissions change
  useEffect(() => {
    if (!topNavRef.current) return;
    // prefer direct active nav item, then active dropdown item, then active dropdown toggle
    const activeEl = topNavRef.current.querySelector('.nav-item.active, .dropdown-item.active, .nav-dropdown.active .nav-dropdown-toggle, .nav-dropdown.open .nav-dropdown-toggle');
    if (activeEl && typeof activeEl.scrollIntoView === 'function') {
      try {
        activeEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      } catch (e) {
        // fallback: set scrollLeft so the item is visible
        const parent = topNavRef.current;
        const rect = activeEl.getBoundingClientRect();
        const parentRect = parent.getBoundingClientRect();
        const offset = rect.left - parentRect.left - (parentRect.width / 2) + (rect.width / 2);
        parent.scrollLeft += offset;
      }
    }
  }, [location.pathname, userRole, loading]);

  if (isAuthRoute || loading) return null;

  const isActive = (paths) => paths.some(p => location.pathname === p);

  // NavItem component for single links
  const NavItem = ({ to, icon: Icon, label, paths }) => (
    <Link to={to} className={`nav-item ${isActive(paths || [to]) ? 'active' : ''}`}>
      {Icon && <Icon className="nav-icon" />}
      <span>{label}</span>
    </Link>
  );

  // NavDropdownItem component for grouped links with delayed hide to prevent flicker
  const NavDropdownGroup = ({ icon: Icon, label, items, activePaths }) => {
    const [open, setOpen] = useState(false);
    const leaveTimer = useRef(null);
    const toggleRef = useRef(null);

    // detect touch devices / small screens where hover doesn't behave well
    const isTouchOrSmall = typeof window !== 'undefined' && (('ontouchstart' in window) || window.matchMedia && window.matchMedia('(hover: none)').matches || window.innerWidth <= 768);

    const handleEnter = () => {
      if (leaveTimer.current) {
        clearTimeout(leaveTimer.current);
        leaveTimer.current = null;
      }
      setOpen(true);
    };

    const handleLeave = () => {
      // delay closing so user can move into submenu
      if (isTouchOrSmall) return; // avoid auto-closing on touch devices
      leaveTimer.current = setTimeout(() => {
        setOpen(false);
        leaveTimer.current = null;
      }, 250);
    };

    const handleToggleClick = (e) => {
      // on touch/small screens toggle on click
      if (!isTouchOrSmall) return;
      e.preventDefault();
      setOpen(prev => !prev);
    };

    // close dropdown when route changes
    useEffect(() => {
      setOpen(false);
      if (leaveTimer.current) {
        clearTimeout(leaveTimer.current);
        leaveTimer.current = null;
      }
    }, [location.pathname]);

    const navigate = useNavigate();

    const handleItemActivate = (e, to) => {
      if (isTouchOrSmall) {
        e.preventDefault();
        navigate(to);
        setOpen(false);
      } else {
        setOpen(false);
      }
    };

    return (
      <div className={`nav-dropdown ${open ? 'open' : ''} ${isActive(activePaths) ? 'active' : ''}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <div
          className="nav-dropdown-toggle"
          ref={toggleRef}
          role="button"
          tabIndex={0}
          onClick={handleToggleClick}
          onPointerDown={(e) => {
            // open immediately on pointer down for touch devices to avoid scroll swallowing the click
            if (isTouchOrSmall) {
              e.preventDefault();
              setOpen(prev => !prev);
            }
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { handleToggleClick(e); } }}
        >
          {Icon && <Icon className="nav-icon" />}
          <span>{label}</span>
          <span className="dropdown-arrow">▾</span>
        </div>
        <div className="nav-dropdown-menu">
          {items.map((item, idx) => (
            <Link
              key={idx}
              to={item.to}
              className={`dropdown-item ${location.pathname === item.to ? 'active' : ''}`}
              onClick={(e) => handleItemActivate(e, item.to)}
              onPointerDown={(e) => { if (isTouchOrSmall) handleItemActivate(e, item.to); }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="top-navigation">
      <div className="top-navigation-inner">
        <nav className="top-nav-links" ref={topNavRef}>
          {userRole === 'customer' ? (
            <>
              <NavItem to="/customer-dashboard" icon={FaTachometerAlt} label="Dashboard" />
              <NavItem to="/my-orders" icon={FaShoppingCart} label="My Orders" />
              <NavItem to="/my-prescriptions" label="Prescriptions" />
              <NavItem to="/my-payments" label="Payments" />
              <NavItem to="/notifications" icon={FaBell} label="Notifications" />
            </>
          ) : (
            <>
              <NavItem to="/dashboard" icon={FaTachometerAlt} label="Dashboard" />

              {/* Users Group */}
              {(hasPermission('manage_users') || hasPermission('manage_roles') || hasPermission('view_customers') || hasPermission('view_delivery_persons')) && (
                <NavDropdownGroup
                  icon={FaUsers}
                  label="Users"
                  activePaths={['/users', '/roles', '/customers', '/delivery-persons']}
                  items={[
                    hasPermission('manage_users') && { to: '/users', label: 'User Management' },
                    hasPermission('manage_roles') && { to: '/roles', label: 'Roles' },
                    hasPermission('view_customers') && { to: '/customers', label: 'Customers' },
                    hasPermission('view_delivery_persons') && { to: '/delivery-persons', label: 'Delivery Persons' },
                  ].filter(Boolean)}
                />
              )}

              {/* Inventory Group */}
              {(hasPermission('manage_medicines') || hasPermission('manage_batches') || hasPermission('manage_suppliers') || hasPermission('manage_purchases')) && (
                <NavDropdownGroup
                  icon={FaCapsules}
                  label="Inventory"
                  activePaths={['/medicines', '/batches', '/suppliers', '/purchases', '/racks']}
                  items={[
                    hasPermission('manage_medicines') && { to: '/medicines', label: 'Medicines' },
                      hasPermission('manage_medicines') && { to: '/racks', label: 'Rack Management' },
                    hasPermission('manage_batches') && { to: '/batches', label: 'Batches' },
                    hasPermission('manage_suppliers') && { to: '/suppliers', label: 'Suppliers' },
                    hasPermission('manage_purchases') && { to: '/purchases', label: 'Purchases' },
                  ].filter(Boolean)}
                />
              )}

              {/* Sales Group */}
              {(hasPermission('manage_orders') || hasPermission('manage_prescriptions') || hasPermission('manage_payments')) && (
                <NavDropdownGroup
                  icon={FaShoppingCart}
                  label="Sales"
                  activePaths={['/orders', '/offline-sell', '/prescriptions', '/payments']}
                  items={[
                    hasPermission('manage_orders') && { to: '/orders', label: 'Orders' },
                    hasPermission('manage_orders') && { to: '/offline-sell', label: 'Offline Sell' },
                    hasPermission('manage_prescriptions') && { to: '/prescriptions', label: 'Prescriptions' },
                    hasPermission('manage_payments') && { to: '/payments', label: 'Payments' },
                  ].filter(Boolean)}
                />
              )}

              {/* Consultations Group */}
              {(hasPermission('manage_appointments') || hasPermission('manage_doctors') || userRole === 'admin' || userRole === 'pharmacist') && (
                <NavDropdownGroup
                  icon={FaCalendarCheck}
                  label="Appointments"
                  activePaths={['/appointments', '/doctors']}
                  items={[
                    (hasPermission('manage_appointments') || userRole === 'admin' || userRole === 'pharmacist') && { to: '/appointments', label: 'Patient Bookings' },
                    (hasPermission('manage_doctors') || userRole === 'admin' || userRole === 'pharmacist') && { to: '/doctors', label: 'Doctor Availability' },
                  ].filter(Boolean)}
                />
              )}

              {/* Delivery Group */}
              {hasPermission('manage_deliveries') && (
                <NavItem to="/deliveries" icon={FaTruck} label="Deliveries" />
              )}

              <NavItem to="/notifications" icon={FaBell} label="Notifications" />
              {userRole === 'admin' && (
                <NavItem to="/settings" icon={FaCog} label="Settings" />
              )}
            </>
          )}
        </nav>
        <div className="top-nav-actions">
          <Dropdown align="end">
            <Dropdown.Toggle variant="light" id="profile-dropdown">
              <div className="profile-avatar-nav">
                {/** show profile image with default fallback */}
                <Image
                  src={profileImage}
                  roundedCircle
                  alt="profile"
                  width={32}
                  height={32}
                  onError={(e) => {
                    e.target.onerror = null; // Prevent infinite loop
                    e.target.src = DEFAULT_PROFILE_IMAGE;
                  }}
                />
              </div>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item as={Link} to="/profile">
                <FaUser className="me-2" /> Profile
              </Dropdown.Item>
              {userRole === 'admin' && (
                <Dropdown.Item as={Link} to="/settings">
                  <FaCog className="me-2" /> Settings & Master Data
                </Dropdown.Item>
              )}
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => {
                // logout: clear tokens and navigate to login
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                window.location.href = '/login';
              }}>
                <FaSignOutAlt className="me-2" /> Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default TopNavigation;
